import { AddressType, SingleAddress } from '@zoocasa/go-search';
import { capitalizeWords, capitalizeLocation } from '@zoocasa/node-kit/strings/capitalize';
import { CITY_INFO_ROUTE } from 'components/dynamic-page/route-matchers';
import { CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE } from 'constants/headers';
import { FeaturesType } from 'contexts';
import {
  createAddressHierarchyPath,
  parseAddressPath,
  ParsedLocation,
  UNSUPPORTED_ADDRESS_TYPES,
} from 'data/addresses';
import { Breadcrumb, createBreadcrumbs } from 'components/breadcrumbs';
import { convertStringToCountryCode } from 'types/countries';
import { NullableType } from 'types/nullable';
import isUserAgentCrawler from 'utils/crawler-agent';
import { removeUndefined } from 'utils/objects';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { generateHreflangFromCanonicalUrl } from 'utils/urls/hreflangUrls';
import { HeadDataType } from 'components/head-data';
import { fetchClientInfoPage, isValidUUID } from 'data/client-info-pages';
import { getListings } from 'data/listing/client-app/fetcher';
import { DATE_DESCENDING_SORT, FILTER_PROPERTY, PropertyTypeFilter } from 'contexts/preferences/listing-params/types';
import { ListingSerializableProperties } from 'data/listing/client-app/types/listing';
import { parseAffiliateHandle } from 'utils/my-link-my-lead/affiliate-handle-processor';
import { standardizedCityName } from 'utils/standardized-city-name';

import type { AddressesApiType } from 'data/addresses';
import type { InsightsApiType } from 'data/insights';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { DynamicPageServerSideProps } from 'types/dynamic_page_types';
import type { CityInfoPageViewModel, AreaInsightsData, CustomPageConfig } from './city_info_page_view_model';
import type { TenantTypes } from '@tenants/manager';
import type { Nullable } from 'types/nullable';
import { provinceOrStateCodeFromString, provinceOrStateNameFromCode } from 'utils/province_or_state';

//#region Types
export type CityInfoPageServerSideProps = Required<Nullable<CityInfoPageViewModel>>;

type GetServerSidePropsReturnType = Promise<
  GetServerSidePropsResult<DynamicPageServerSideProps<CityInfoPageServerSideProps>>
>;
//#endregion

//#region Constants
const DEFAULT_NOT_FOUND_RESPONSE: GetServerSidePropsResult<
  DynamicPageServerSideProps<CityInfoPageServerSideProps>
> = { notFound: true };
//#endregion

/** Verbose per-step tracing, off by default — set CITY_INFO_DEBUG=true to enable in server logs. */
function debugLog(...args: unknown[]): void {
  if (process.env.CITY_INFO_DEBUG === 'true') {
    console.log(...args);
  }
}

/**
 * Generates a simple heading for the city info page.
 * Format: "[City Name], [Province/State]" or just "[Area Name]"
 */
function generateCityInfoHeading(address: SingleAddress): string {
  const parts: string[] = [];
  // A province/state-level address (e.g. the "ontario-info" page) has no neighbourhood or
  // subDivision — its own provinceOrState IS the area being shown, so it must not also be
  // appended as a suffix (that previously produced "Ontario, ON" instead of just "Ontario").
  const isCityOrNeighbourhoodLevel = Boolean(address.neighbourhood || address.subDivision);

  if (address.neighbourhood) {
    parts.push(address.neighbourhood);
  } else if (address.subDivision) {
    parts.push(address.subDivision);
  } else if (address.provinceOrState) {
    // address.provinceOrState is a 2-letter code (e.g. "on") — resolve to the full name
    // ("Ontario") for a province-level page heading; fall back to the raw code if unrecognized.
    const provinceCode = provinceOrStateCodeFromString(address.provinceOrState);
    parts.push(provinceCode ? provinceOrStateNameFromCode(provinceCode) : address.provinceOrState);
  } else if (address.country) {
    parts.push(address.country);
  }

  if (isCityOrNeighbourhoodLevel && address.provinceOrState) {
    parts.push(address.provinceOrState);
  }

  // capitalizeWords would title-case "on" (Ontario's code) down to lowercase — it treats "on" as
  // the English preposition. capitalizeLocation upper-cases everything after the last comma instead.
  return capitalizeLocation(parts.join(', '));
}

/**
 * Generates the area blurb heading based on the address components.
 */
function generateAreaBlurbHeading(
  subDivision?: string,
  neighbourhood?: string,
  provinceOrState?: string
): string | undefined {
  if (neighbourhood) {
    return `About ${capitalizeWords(neighbourhood)}`;
  }
  if (subDivision) {
    return `About ${capitalizeWords(subDivision)}`;
  }
  if (provinceOrState) {
    return `About ${capitalizeWords(provinceOrState)}`;
  }
  return undefined;
}

/**
 * Builds a property type filter from client preferences.
 */
function buildPropertyTypeFilter(propertyTypes?: string[]): PropertyTypeFilter {
  const defaultFilter: PropertyTypeFilter = {
    house: true,
    townhouse: true,
    condo: true,
    land: false,
    commercial: false,
    farm: false,
  };

  if (!propertyTypes || propertyTypes.length === 0) {
    return defaultFilter;
  }

  // If specific property types are set, only enable those
  return {
    house: propertyTypes.includes('house'),
    townhouse: propertyTypes.includes('townhouse'),
    condo: propertyTypes.includes('condo'),
    land: propertyTypes.includes('land'),
    commercial: propertyTypes.includes('commercial'),
    farm: propertyTypes.includes('farm'),
  };
}

/**
 * Converts bedrooms/bathrooms number to filter string format (e.g., "3+")
 */
function toFilterString(value?: number): string {
  if (!value || value <= 0) return '0+';
  return `${value}+`;
}

export default async function getServerSideProps(
  context: GetServerSidePropsContext,
  features: FeaturesType,
  tenant: TenantTypes,
  addressesApi: AddressesApiType,
  insightsApi?: InsightsApiType
): GetServerSidePropsReturnType {
  context.res.setHeader(CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE);

  // Parse the URL path - URLs are now directly /toronto-on-info/{uuid} without /city-info prefix
  let sanitizedUrl = context.resolvedUrl;
  // Remove locale prefix if present (e.g., /en/toronto-on-info -> /toronto-on-info)
  if (sanitizedUrl.match(/^\/[a-z]{2}(-[a-z]{2})?\//)) {
    sanitizedUrl = sanitizedUrl.replace(/^\/[a-z]{2}(-[a-z]{2})?\//, '/');
  }
  // Strip the internal /city-info rewrite prefix. The proxy rewrites public
  // /{area}-info URLs to /city-info/{area}-info so the dedicated page can serve
  // them; downstream parsing expects the clean, prefix-less public path.
  if (sanitizedUrl.startsWith('/city-info/')) {
    sanitizedUrl = sanitizedUrl.slice('/city-info'.length);
  }
  let detectedHandle: string | null = null;

  // Check for affiliate handle at the start of the path (e.g., /@handle/toronto-on-info/{uuid})
  const handleMatch = parseAffiliateHandle(sanitizedUrl);
  if (handleMatch && handleMatch.position === 'start') {
    detectedHandle = handleMatch.handle;
    // Remove the handle from the path for further processing
    sanitizedUrl = handleMatch.remainingPath || sanitizedUrl;
    debugLog('[CityInfoPage] Detected handle:', detectedHandle);
    debugLog('[CityInfoPage] Remaining path after handle:', sanitizedUrl);
  }

  debugLog('[CityInfoPage] resolvedUrl:', context.resolvedUrl);
  debugLog('[CityInfoPage] sanitizedUrl:', sanitizedUrl);

  // Check if the URL contains a UUID (custom page)
  const urlParts = sanitizedUrl.split('/').filter(Boolean);
  const lastPart = urlParts[urlParts.length - 1];
  const isCustomPage = isValidUUID(lastPart);

  debugLog('[CityInfoPage] urlParts:', urlParts);
  debugLog('[CityInfoPage] lastPart:', lastPart);
  debugLog('[CityInfoPage] isCustomPage:', isCustomPage);

  let customPageConfig: CustomPageConfig | null = null;
  let customPageAreaSlug: string | null = null;

  if (isCustomPage) {
    debugLog('[CityInfoPage] Fetching custom page for UUID:', lastPart);
    // Fetch the custom page configuration from agent-admin
    customPageConfig = await fetchClientInfoPage(lastPart);

    debugLog('[CityInfoPage] customPageConfig result:', customPageConfig ? 'Found' : 'Not found');

    if (!customPageConfig) {
      // Custom page not found or expired
      debugLog('[CityInfoPage] Returning 404 - custom page not found');
      return DEFAULT_NOT_FOUND_RESPONSE;
    }

    // Use the area_slug from the custom page config
    customPageAreaSlug = customPageConfig.area_slug;
    debugLog('[CityInfoPage] customPageAreaSlug:', customPageAreaSlug);

    // Handle affiliate handle validation and attribution
    const agentHandle = customPageConfig.agent?.affiliate_handle;
    
    if (detectedHandle) {
      // Handle is in URL - verify it matches the agent's handle
      if (agentHandle && detectedHandle.toLowerCase() !== agentHandle.toLowerCase()) {
        // Handle doesn't match - redirect to correct URL with agent's handle
        debugLog('[CityInfoPage] Handle mismatch, redirecting to correct URL');
        // Prefix-less public URL; the proxy rewrites it to the /city-info page.
        const correctUrl = agentHandle
          ? `/@${agentHandle}/${customPageAreaSlug}-info/${lastPart}`
          : `/${customPageAreaSlug}-info/${lastPart}`;
        return {
          redirect: {
            permanent: false,
            destination: correctUrl,
          },
        };
      }
      // Handle matches or agent has no handle - continue (attribution will be handled by middleware)
    } else if (agentHandle) {
      // No handle in URL but agent has one - redirect to include handle
      debugLog('[CityInfoPage] Agent has handle but URL missing it, redirecting');
      const correctUrl = `/@${agentHandle}/${customPageAreaSlug}-info/${lastPart}`;
      return {
        redirect: {
          permanent: false,
          destination: correctUrl,
        },
      };
    }
  }

  // For custom pages, use the area slug from config; otherwise parse from URL
  const urlForParsing = customPageAreaSlug
    ? `/${customPageAreaSlug}-info`
    : sanitizedUrl;
  const parsedAreaPathFromUrl = parseAddressPath(urlForParsing, 'info');

  if (parsedAreaPathFromUrl.length === 0) {
    return DEFAULT_NOT_FOUND_RESPONSE;
  }

  // Get the requested address from parsed path
  const requestedAddress =
    parsedAreaPathFromUrl.length === 1
      ? parsedAreaPathFromUrl[0]
      : parsedAreaPathFromUrl.filter(
        segment => segment.type === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD
      )[0];
  const requestedAreaSlug: string = requestedAddress.slug;

  // Fetch the address from the API
  const requestedAreaPageAddress = await addressesApi.getAddressesBySlug(requestedAreaSlug);
  let fallbackAreaPageAddress: NullableType<SingleAddress[]> = null;
  let electedAreaPageAddress: SingleAddress[];

  const isUnknownAddress = requestedAreaPageAddress?.length === 0;
  if (isUnknownAddress) {
    // The requested address cannot be found, we need to fallback to a different address.
    const addressSegments: (ParsedLocation | undefined)[] = [
      requestedAddress.neighbourhood,
      requestedAddress.subDivision,
      requestedAddress.provinceOrState,
      requestedAddress.country,
    ];
    for (const addressSegment of addressSegments) {
      if (addressSegment) {
        const address = await addressesApi.getAddressesBySlug(addressSegment.slug);
        if (address?.length > 0) {
          fallbackAreaPageAddress = address;
          break;
        }
      }
    }
    if (!fallbackAreaPageAddress || fallbackAreaPageAddress.length === 0) {
      // Nothing in the requested slug's own hierarchy resolves to a real address —
      // don't silently substitute a generic country page for a bogus/garbage slug.
      return DEFAULT_NOT_FOUND_RESPONSE;
    }
    electedAreaPageAddress = fallbackAreaPageAddress;
  } else {
    electedAreaPageAddress = requestedAreaPageAddress;
  }

  const electedAreaPageAddressHierarchy = createAddressHierarchyPath(electedAreaPageAddress);
  if (!electedAreaPageAddressHierarchy) {
    return DEFAULT_NOT_FOUND_RESPONSE;
  }

  if (UNSUPPORTED_ADDRESS_TYPES.includes(electedAreaPageAddressHierarchy.type)) {
    console.error(
      `city info page received a request to display an address that is not supported: ${requestedAreaSlug}`
    );
    return DEFAULT_NOT_FOUND_RESPONSE;
  }

  const electedAddress = electedAreaPageAddress[electedAreaPageAddress.length - 1];
  const breadcrumbs: readonly Breadcrumb[] | undefined = createBreadcrumbs(
    electedAreaPageAddressHierarchy
  );

  // Generate heading - personalized for custom pages
  const baseHeading = generateCityInfoHeading(electedAddress);
  const capitalizedClientName = customPageConfig?.client_name
    ?.replace(/\b\w/g, (c: string) => c.toUpperCase());
  const heading = isCustomPage && customPageConfig
    ? `${baseHeading} for ${capitalizedClientName}`
    : baseHeading;

  const isCrawler = isUserAgentCrawler(context.req.headers);

  // Fetch area insights data if insightsApi is available
  let areaInsightsData: AreaInsightsData | undefined;
  let areaName: string | undefined;
  let areaBlurbHeading: string | undefined;

  const country = convertStringToCountryCode(electedAddress.countryCode);
  const provinceOrState = electedAddress.provinceOrState
    ? provinceOrStateCodeFromString(electedAddress.provinceOrState) ?? undefined
    : undefined;

  if (insightsApi && !isUnknownAddress && (!country || !provinceOrState)) {
    console.warn(`[CityInfoPage] Unrecognized country/province code (countryCode="${electedAddress.countryCode}", provinceOrState="${electedAddress.provinceOrState}") — skipping insights fetch for ${requestedAreaSlug}`);
  }

  // Province-level pages (e.g. ontario-info) have no subDivision to key city-level stats off of —
  // getCityStats accepts city as undefined and returns province-wide stats in that case.
  if (insightsApi && !isUnknownAddress && country && provinceOrState) {
    const city = electedAddress.subDivision;

    try {
      debugLog('[CityInfoPage] Fetching insights for:', { country, provinceOrState, city });

      // Fetch city stats and top cities in parallel
      const [cityStats, topCities] = await Promise.all([
        insightsApi.getCityStats(country, provinceOrState, city),
        insightsApi.getTopCities(country, provinceOrState),
      ]);

      debugLog('[CityInfoPage] Insights data received:', {
        hasCityStats: !!cityStats,
        MedianPurchasePrice: cityStats?.MedianPurchasePrice,
        DaysOnMarket: cityStats?.DaysOnMarket,
        AvgPricePerSqft: cityStats?.AvgPricePerSqft,
        MedianRentPrice: cityStats?.MedianRentPrice,
        TotalHouses: cityStats?.TotalHouses,
        TotalCondos: cityStats?.TotalCondos,
        TotalTownhouses: cityStats?.TotalTownhouses,
        TotalRentals: cityStats?.TotalRentals,
        SellListRatio: cityStats?.SellListRatio,
        hasTopCities: !!topCities,
        topCitiesCount: topCities?.length,
      });

      areaInsightsData = {
        areaData: cityStats,
        surroundingCities: topCities?.map(topCity => ({
          city: standardizedCityName(topCity.Name),
          province: topCity.Province,
          slug: topCity.Slug,
        })),
      };

      // FAQ accordion questions read oddly with a bare city name ("...in Toronto?") —
      // append the province/state code ("Toronto, ON") when we have one. electedAddress.provinceOrState
      // is already a short code (e.g. "on"), same value used above for the insights API call.
      // Only do this at city/neighbourhood level — a province-level address (e.g. the
      // "ontario-info" page) IS the province, so appending its own code would wrongly
      // produce "Ontario, ON" instead of just "Ontario".
      const areaLabel = electedAddress.label || heading;
      const isCityOrNeighbourhoodLevel = Boolean(electedAddress.subDivision || electedAddress.neighbourhood);
      areaName = isCityOrNeighbourhoodLevel && electedAddress.provinceOrState
        ? `${areaLabel}, ${electedAddress.provinceOrState.toUpperCase()}`
        : areaLabel;
      areaBlurbHeading = generateAreaBlurbHeading(
        requestedAddress?.subDivision?.name,
        requestedAddress?.neighbourhood?.name,
        requestedAddress?.provinceOrState?.name
      );
    } catch (error) {
      console.error('[CityInfoPage] Failed to fetch area insights data:', error);
    }
  }

  // Fetch listings for the carousel — custom pages get client-preference-filtered
  // recommendations, plain pages get a general "homes for sale in this area" set.
  let recommendedListings: ListingSerializableProperties[] = [];

  if (isCustomPage && customPageConfig) {
    try {
      const preferences = customPageConfig.client_preferences;

      // Build filter from client preferences
      const listingFilter = {
        slug: customPageConfig.area_slug,
        homeType: buildPropertyTypeFilter(preferences.property_types),
        priceMin: preferences.budget_min ? Number(preferences.budget_min) : null,
        priceMax: preferences.budget_max ? Number(preferences.budget_max) : null,
        bedrooms: toFilterString(preferences.min_bedrooms ? Number(preferences.min_bedrooms) : undefined),
        bathrooms: toFilterString(preferences.min_bathrooms ? Number(preferences.min_bathrooms) : undefined),
        status: {
          available: true,
          notAvailable: false,
          notAvailableSold: false,
          notAvailableOther: false,
          deleted: false,
        },
      };

      debugLog('[CityInfoPage] Fetching recommended listings with filter:', listingFilter);

      const { listings } = await getListings({
        [FILTER_PROPERTY]: listingFilter,
        page: { number: 1, size: 12 },
      });

      // Convert to serializable format for SSR
      recommendedListings = listings
        .filter(listing => listing.isImageReady)
        .map(listing => listing.toJSON());
      debugLog('[CityInfoPage] Found', recommendedListings.length, 'recommended listings');
    } catch (error) {
      console.error('Failed to fetch recommended listings:', error);
    }
  } else if (!isUnknownAddress) {
    try {
      const { listings } = await getListings({
        [FILTER_PROPERTY]: {
          slug: requestedAreaSlug,
          homeType: buildPropertyTypeFilter(),
          status: {
            available: true,
            notAvailable: false,
            notAvailableSold: false,
            notAvailableOther: false,
            deleted: false,
          },
        },
        // Without an explicit sort, page 1 comes back price-ascending — the
        // cheapest results are $1-$8 data-entry-error/placeholder listings.
        // Same sort the homepage carousel uses (newest first).
        sort: DATE_DESCENDING_SORT,
        page: { number: 1, size: 12 },
      });

      // Same junk-listing guard as the recommended-listings fetch above —
      // $1/$4 placeholder/data-entry-error listings pass every other filter.
      recommendedListings = listings
        .filter(listing => listing.isImageReady && (listing.price === null || listing.price >= 10000))
        .map(listing => listing.toJSON());
    } catch (error) {
      console.error('Failed to fetch area listings:', error);
    }
  }

  // Generate SEO head tags
  const theme = tenant.theme;
  const canonicalUrl = generateCanonicalUrl({
    asPath: sanitizedUrl.replace('-real-estate', '-info'),
    baseHost: theme.schemaUrl,
    locale: context.locale || tenant.localization.defaultLanguage,
    tenantId: tenant.id,
  });

  const hrefLangs = generateHreflangFromCanonicalUrl({
    canonicalUrl,
    locale: context.locale || tenant.localization.defaultLanguage,
    tenantId: tenant.id,
  });

  // Enhanced SEO description with market data if available
  const medianPrice = areaInsightsData?.areaData?.MedianPurchasePrice;
  const metaDescription = isCustomPage
    ? `Personalized area information for ${capitalizedClientName}. View market statistics, price trends, and neighbourhood insights.`
    : medianPrice
      ? `Explore ${baseHeading} - median home price $${Math.round(medianPrice).toLocaleString()}. View market statistics, price trends, property inventory, and neighbourhood insights.`
      : `Learn about ${baseHeading}. Discover city information, market statistics, neighbourhood details, and more.`;

  const headTags: HeadDataType = {
    title: `${heading} - City Information | ${theme.name}`,
    metaDescription,
    canonicalUrl,
    hrefLangs,
    // Custom pages should not be indexed by search engines
    ...(isCustomPage && { robots: 'noindex, nofollow' }),
  };

  return {
    props: {
      routeName: CITY_INFO_ROUTE,
      props: {
        features,
        tenantId: tenant.id,
        headTags: removeUndefined(headTags),
        requestedAddress: removeUndefined(requestedAddress),
        electedAddress: removeUndefined(electedAddress),
        breadcrumbs: breadcrumbs || [],
        heading,
        isCrawler,
        areaInsightsData: areaInsightsData ? removeUndefined(areaInsightsData) : null,
        areaName: areaName || null,
        areaBlurb: null, // TODO: Fetch from namedContentsApi in future
        areaBlurbHeading: areaBlurbHeading || null,
        isCustomPage,
        customPageConfig: customPageConfig ? removeUndefined(customPageConfig) : null,
        recommendedListings: recommendedListings.length > 0 ? recommendedListings : null,
      },
    },
  };
}
